import { Injectable, inject } from '@angular/core';
import { HistoryRepository } from 'src/app/analytics/state/history.repository';
import { SettingsRepository } from 'src/app/settings/state/settings.repository';
import { dayKeyFor } from 'src/app/utilities/day-key.utils';
import { partitionEntriesByDay } from 'src/app/utilities/day-partition.utils';
import { itemsEqual } from 'src/app/utilities/sync-arbitration.utils';

export interface RepairResult {
  moved: number;
  removed: number;
}

/**
 * One-time repair for days corrupted before entries were routed by their own timestamp.
 *
 * Before this branch, `addEntryAndSync` could read the *active* day's timestamp array and write it
 * under the *wall-clock* day key, copying an entire previous day's timeline into a phantom "today".
 * Tasks 3-8 made that structurally impossible going forward; this service repairs the damage
 * already sitting in a device's localStorage.
 *
 * Partitions every stored day by `dayKeyFor(entry.timestamp)`, merges each partition into its
 * correct day de-duplicated by entry id, and drops days left empty. The phantom duplicates carry
 * the same ids as their originals, so a phantom day collapses to nothing without losing an entry.
 *
 * Deliberately a no-op (no store write at all, not even a re-sort) for any day whose repaired
 * content is identical to what is already stored (compared via `itemsEqual`).
 *
 * Still gated by a one-time version guard (`repairOnce`, see below) rather than relying on that
 * per-day no-op check alone: idempotency makes a REPEATED run of `repair()` itself cheap and safe,
 * but it does not make `repair()` safe to keep running forever against a LIVE, user-editable
 * `dayStartHour` — every call re-derives every entry's day key from whatever hour is set *right
 * now*, so changing "Day Starts At" and relaunching would otherwise silently re-partition the
 * user's entire history on the next launch. `repairOnce()` is what stops that: it runs the body
 * exactly once per install, keyed only on a version counter, deliberately never re-armed by a
 * later `dayStartHour` change (see the design doc: "Changing it re-derives day keys going forward;
 * it does not retroactively repartition history").
 */
@Injectable({ providedIn: 'root' })
export class HistoryRepairService {
  private static readonly REPAIR_VERSION = 1;

  // Constructor-parameter injection (with inject() only as the default, not a field initializer) is
  // deliberate here, not an oversight: specs construct this directly via `new HistoryRepairService(
  // history, settingsStub)`, and a field-initializer inject() throws NG0203 outside an injection
  // context (see HistoryRepository for the same constraint). Real DI resolves these constructor
  // parameters by type without ever evaluating the default.
  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject -- see comment above
    private history: HistoryRepository = inject(HistoryRepository),
    // eslint-disable-next-line @angular-eslint/prefer-inject -- see comment above
    private settings: SettingsRepository = inject(SettingsRepository),
  ) {}

  /**
   * Runs `repair()` at most once per `REPAIR_VERSION`, gated ONLY on that version counter — never
   * on `dayStartHour`. Call this from app startup instead of `repair()` directly.
   */
  repairOnce(): RepairResult | null {
    if (this.settings.getHistoryRepairVersion() >= HistoryRepairService.REPAIR_VERSION) {
      return null;
    }
    const result = this.repair();
    this.settings.setHistoryRepairVersion(HistoryRepairService.REPAIR_VERSION);
    return result;
  }

  repair(): RepairResult {
    const hour = this.settings.getDayStartHour();
    const days = this.history.getEntries();

    let moved = 0;
    for (const day of days) {
      for (const entry of day.timestamps ?? []) {
        if (dayKeyFor(entry.timestamp, hour) !== day.id) {
          moved++;
        }
      }
    }

    const byDay = partitionEntriesByDay(
      days.flatMap((day) => day.timestamps ?? []),
      hour,
    );

    let removed = 0;

    // Existing days: rewrite only when the repaired content actually differs from what is stored.
    for (const day of days) {
      const repaired = byDay.get(day.id) ?? [];
      const original = day.timestamps ?? [];
      if (itemsEqual(original, repaired)) {
        continue;
      }
      // Only count a day as "removed" when it will actually vanish (mirrors dropEmptyDays' own
      // condition below): a day that loses all its entries but keeps a user-set goal survives, so
      // it must not be reported as removed.
      if (original.length > 0 && repaired.length === 0 && day.goal == null) {
        removed++;
      }
      this.history.updateEntities(day.id, repaired, hour);
    }

    // New days: entries relocated to a day with no existing local entity.
    for (const [dayKey, entries] of byDay) {
      if (!this.history.getDay(dayKey)) {
        this.history.updateEntities(dayKey, entries, hour);
      }
    }

    this.history.dropEmptyDays();

    return { moved, removed };
  }
}
